import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { Plus, Upload } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExcelUploadDialog,
  Page,
  PageContent,
  UnsavedChangesConfirmDialog
} from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { useBulkAddProcesses } from '@/api/mutations/useBulkAddProcesses';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { usePageUnsavedChanges } from '@/hooks/usePageUnsavedChanges';
import { translateErrorMessage } from '@/lib/errors';
import { downloadProcessesTemplate } from '@/lib/excel';
import {
  ProcessDraftTable,
  validateProcessData,
  parseProcessesFromExcel,
  type BulkProcessRowInput,
  type ProcessDraft,
  type ProcessDraftBase
} from '@/components/bulk-upload';
import { resolveDepartmentToId } from '@/lib/department-ref';
import { CrudErrorAlert } from '@/components/crud/CrudErrorAlert';
import { ProcessFormDialog } from '@/pages/processes/all-processes/components/ProcessFormDialog';
import type { ProcessInput, AddProcessRequest } from '@bliks-io/contracts';
import type { ApiError } from '@/lib/agent.types';
import { queryClient } from '@/lib';
import { processKeys } from '@/api/query-keys';

// The wizard's drafts are strict-parsed as ProcessInput, so form-only fields
// (apqcEquivalentId) must never leak into them. Accountable is part of
// ProcessInput; the Excel import supplies it via an optional email column
// resolved at the import boundary.
const toProcessInput = ({
  apqcEquivalentId: _apqcEquivalentId,
  ...processInput
}: AddProcessRequest): ProcessInput => processInput;

export function CreateProcessesPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.createProcesses');
  const navigate = useNavigate();
  const slug = useAuthStore().slug();
  const bulkAddProcessesMutation = useBulkAddProcesses();

  const [drafts, setDrafts] = useState<ProcessDraft[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingDraft, setEditingDraft] = useState<ProcessDraft | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isManualFormOpen, setIsManualFormOpen] = useState(false);
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Ref to track when we're saving (to prevent blocking navigation on successful save)
  const isSavingRef = useRef(false);

  // Fetch departments for the table display
  const { data: departments } = useDepartments();
  // Members resolve the Excel import's optional accountable email to a user id
  const { data: orgMembers } = useOrgMembers();

  // Unsaved changes handling
  const { showConfirmation, handleNavigateWithConfirmation, handleStay, handleDiscard } =
    usePageUnsavedChanges({
      hasUnsavedChanges: drafts.length > 0 && !isSavingRef.current,
      onNavigateAway: () => navigate({ to: '/$slug/processes/all-processes', params: { slug } })
    });

  // Add process from form
  const handleAddProcess = (process: ProcessInput) => {
    const validation = validateProcessData(process);
    const newDraft: ProcessDraft = {
      id: uuidv4(),
      data: process,
      isValid: validation.isValid,
      errors: validation.errors,
      rowNumber: drafts.length + 1
    };
    setDrafts([...drafts, newDraft]);
    setIsManualFormOpen(false);
  };

  // Add processes from Excel
  const handleProcessesFromExcel = (newDrafts: ProcessDraftBase<BulkProcessRowInput>[]) => {
    // Store the department as its stable id at the import boundary (the manual
    // form already stores an id), so the field is always an id: the edit
    // dialog's id-keyed dropdown pre-selects, and a rename would follow. An
    // unresolved abbreviation is kept so the row still flags it. The optional
    // accountable email resolves to a user id the same way — an email that
    // matches no member flags the row and leaves the field on the fallback.
    const resolved: ProcessDraft[] = newDrafts.map((draft) => {
      const { accountableEmail, ...data } = draft.data;
      const accountableMember = accountableEmail
        ? orgMembers?.find((m) => m.email?.toLowerCase() === accountableEmail.toLowerCase())
        : undefined;
      const accountableUnresolved = Boolean(accountableEmail) && !accountableMember;
      return {
        ...draft,
        data: {
          ...data,
          department: resolveDepartmentToId(draft.data.department, departments ?? []),
          ...(accountableMember && { accountable: accountableMember._id })
        },
        isValid: draft.isValid && !accountableUnresolved,
        errors: accountableUnresolved
          ? [
              ...(draft.errors ?? []),
              {
                field: 'accountableEmail',
                message: t('bulkOnboarding.validation.accountableEmailNotFound', {
                  email: accountableEmail
                })
              }
            ]
          : draft.errors
      };
    });
    setDrafts([...drafts, ...resolved]);
  };

  // Edit draft
  const handleEditDraft = (draft: ProcessDraft) => {
    setEditingDraft(draft);
  };

  const handleSaveEdit = (changedFields: Partial<ProcessInput>) => {
    if (!editingDraft) return;

    // FormDialog returns only the dirty fields on edit — merge them onto the
    // existing draft so untouched fields (e.g. name/description when only the
    // department changed) are preserved instead of cleared.
    const mergedData = { ...editingDraft.data, ...changedFields };
    const validation = validateProcessData(mergedData);
    setDrafts(
      drafts.map((d) =>
        d.id === editingDraft.id
          ? {
              ...d,
              data: mergedData,
              isValid: validation.isValid,
              errors: validation.errors
            }
          : d
      )
    );
    setEditingDraft(null);
  };

  // Delete draft
  const handleDeleteDraft = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (!deleteConfirmId) return;
    setDrafts(drafts.filter((d) => d.id !== deleteConfirmId));
    setDeleteConfirmId(null);
  };

  // Create all processes using bulk endpoint
  const handleCreateAll = async () => {
    // Only create valid drafts
    const validDrafts = drafts.filter((d) => d.isValid);
    if (validDrafts.length === 0) return;

    setIsCreating(true);
    isSavingRef.current = true;
    setError(undefined);

    try {
      // Use bulk endpoint - single API call for all processes
      await bulkAddProcessesMutation.mutateAsync({
        processes: validDrafts.map((d) => d.data)
      });
      queryClient.invalidateQueries({ queryKey: processKeys.list() });
      toast.success(
        t('processes.createProcessesPage.successCreated', { count: validDrafts.length })
      );
      navigate({ to: '/$slug/processes/all-processes', params: { slug } });
    } catch (err) {
      console.error('Error creating processes:', err);
      isSavingRef.current = false;
      const apiError = err as ApiError;
      const errorMessage = translateErrorMessage(apiError, t);

      // Try to map error back to specific drafts based on metadata
      let errorMappedToDrafts = false;

      if (apiError.metadata?.departments && Array.isArray(apiError.metadata.departments)) {
        // Handle department-related errors (notFound, noManager)
        const failedDepartments = apiError.metadata.departments as string[];

        setDrafts((currentDrafts) =>
          currentDrafts.map((draft) => {
            // Find the department object to check by name/abbreviation
            const draftDept = departments?.find((d) => d._id === draft.data.department);

            // Check if this draft's department matches any failed department
            const isDeptFailed =
              failedDepartments.includes(draft.data.department) ||
              (draftDept && failedDepartments.includes(draftDept.name)) ||
              (draftDept && failedDepartments.includes(draftDept.abbreviation));

            if (isDeptFailed) {
              errorMappedToDrafts = true;
              return {
                ...draft,
                isValid: false,
                errors: [...(draft.errors || []), { field: 'department', message: errorMessage }]
              };
            }
            return draft;
          })
        );
      }

      // Fallback: show general error alert if we couldn't map to specific drafts
      if (!errorMappedToDrafts) {
        setError(errorMessage);
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    handleNavigateWithConfirmation();
  };

  return (
    <Page>
      <PageContent>
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-xl font-semibold leading-7">
              {t('processes.createProcessesPage.title')}
            </h1>
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('processes.createProcessesPage.description')}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" size="medium" onClick={() => setIsExcelUploadOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              {t('processes.createProcessesPage.uploadFromExcel')}
            </Button>
            <Button variant="primary" size="medium" onClick={() => setIsManualFormOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('processes.createProcessesPage.addManually')}
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        <CrudErrorAlert error={error} onDismiss={() => setError(undefined)} className="mb-6" />

        {/* Draft Processes Table */}
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold">
            {t('processes.createProcessesPage.draftProcesses')} ({drafts.length})
          </h2>
          <ProcessDraftTable
            drafts={drafts}
            onEdit={handleEditDraft}
            onDelete={handleDeleteDraft}
            departments={departments}
          />
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-end gap-4 pt-4 border-t">
          <Button variant="outline" size="medium" onClick={handleCancel}>
            {t('processes.createProcessesPage.cancel')}
          </Button>
          <div className="flex flex-col items-end gap-2">
            {drafts.some((d) => !d.isValid) && (
              <p className="text-sm text-warning">
                {t('processes.createProcessesPage.fixErrorsWarning')}
              </p>
            )}
            <Button
              variant="primary"
              size="medium"
              onClick={handleCreateAll}
              disabled={drafts.length === 0 || drafts.some((d) => !d.isValid) || isCreating}
              loading={isCreating}
            >
              {t('processes.createProcessesPage.createAll')} (
              {drafts.filter((d) => d.isValid).length}/{drafts.length})
            </Button>
          </div>
        </div>

        {/* Edit Dialog */}
        {editingDraft && (
          <ProcessFormDialog
            open={!!editingDraft}
            onOpenChange={() => setEditingDraft(null)}
            // TODO: Review AI Code for feature: Process Map
            onSubmit={(data: AddProcessRequest) => handleSaveEdit(toProcessInput(data))}
            initialData={editingDraft.data}
            isEditing
            autoPopulateOwnerFromManager
            title={t('processes.createProcessesPage.editProcessTitle')}
            description={t('processes.createProcessesPage.editProcessDescription')}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
              <DialogDescription>
                {t('processes.createProcessesPage.confirmDelete')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                {t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Form Dialog */}
        <ProcessFormDialog
          open={isManualFormOpen}
          onOpenChange={setIsManualFormOpen}
          onSubmit={(data: AddProcessRequest) => handleAddProcess(toProcessInput(data))}
          autoPopulateOwnerFromManager
          title={t('processes.createProcessesPage.addManually')}
          description={t('processes.createProcessesPage.addManuallyDescription')}
        />

        {/* Excel Upload Dialog */}
        <ExcelUploadDialog
          open={isExcelUploadOpen}
          onOpenChange={setIsExcelUploadOpen}
          title={t('processes.createProcessesPage.uploadFromExcel')}
          description={t('processes.createProcessesPage.uploadFromExcelDescription')}
          downloadTemplate={downloadProcessesTemplate}
          parse={async (file) => {
            const result = await parseProcessesFromExcel(file);
            handleProcessesFromExcel(result.drafts);
            return result.validRows;
          }}
        />

        {/* Unsaved Changes Confirmation Dialog */}
        <UnsavedChangesConfirmDialog
          open={showConfirmation}
          onStay={handleStay}
          onDiscard={handleDiscard}
        />
      </PageContent>
    </Page>
  );
}
